import React, { useEffect, useMemo, useState } from 'react';
import './MileageEstimator.css';

const getPercentileValue = (sortedValues, percentile) => {
    if (!sortedValues.length) return null;

    const index = (sortedValues.length - 1) * percentile;
    const lowerIndex = Math.floor(index);
    const upperIndex = Math.ceil(index);

    if (lowerIndex === upperIndex) {
        return sortedValues[lowerIndex];
    }

    const weight = index - lowerIndex;
    return sortedValues[lowerIndex] * (1 - weight) + sortedValues[upperIndex] * weight;
};

const createBand = (label, low, high, tone) => ({
    label,
    low,
    high,
    tone
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ConditionEstimator = ({ filteredAuctions, onGraphToggleChange, onBandsChange }) => {
    const [showOnGraph, setShowOnGraph] = useState(false);

    const conditionData = useMemo(() => {
        const validPrices = (filteredAuctions || [])
            .map(auction => {
                const price = typeof auction.finalBid === 'number'
                    ? auction.finalBid
                    : parseFloat(String(auction.finalBid).replace(/[^0-9.]/g, ''));
                return Number.isFinite(price) && price > 0 ? price : null;
            })
            .filter(price => price != null)
            .sort((a, b) => a - b);

        const sampleSize = validPrices.length;

        if (sampleSize < 5) {
            return {
                sampleSize,
                bands: []
            };
        }

        const minPrice = validPrices[0];
        const maxPrice = validPrices[validPrices.length - 1];
        const medianPrice = getPercentileValue(validPrices, 0.5);
        const spreadRatio = medianPrice > 0 ? (maxPrice - minPrice) / medianPrice : 0;
        const normalizedSpread = clamp((spreadRatio - 0.8) / 1.2, 0, 1);
        const lowerPercentile = clamp(0.25 - (normalizedSpread * 0.05), 0.2, 0.25);
        const upperPercentile = clamp(0.85 + (normalizedSpread * 0.05), 0.85, 0.9);

        const belowLow = minPrice;
        const belowHigh = getPercentileValue(validPrices, lowerPercentile);
        const coreLow = getPercentileValue(validPrices, lowerPercentile);
        const coreHigh = getPercentileValue(validPrices, upperPercentile);
        const premiumLow = getPercentileValue(validPrices, upperPercentile);
        const premiumHigh = maxPrice;

        return {
            sampleSize,
            lowerPercentile,
            upperPercentile,
            bands: [
                createBand('Premium', premiumLow, premiumHigh, '#a855f7'),
                createBand('Core Market', coreLow, coreHigh, '#22c55e'),
                createBand('Below Average', belowLow, belowHigh, '#eab308')
            ]
        };
    }, [filteredAuctions]);

    const formatPrice = (price) => {
        if (!Number.isFinite(price)) return '--';
        const rounded = Math.round(price / 100) * 100;
        return `$${rounded.toLocaleString()}`;
    };

    useEffect(() => {
        if (!onGraphToggleChange) return;
        onGraphToggleChange(showOnGraph);
    }, [showOnGraph, onGraphToggleChange]);

    useEffect(() => {
        if (!onBandsChange) return;
        onBandsChange(conditionData.sampleSize >= 5 ? conditionData.bands : []);
    }, [conditionData, onBandsChange]);

    if (conditionData.sampleSize < 5) {
        return (
            <div className="mileage-estimator-tile">
                <h3 className="estimator-title">Price Guide</h3>
                <p className="estimator-no-data">
                    Not enough filtered results to estimate.
                    Need at least 5 sold auctions.
                </p>
            </div>
        );
    }

    return (
        <div className="mileage-estimator-tile">
            <h3 className="estimator-title">Price Guide</h3>
            <p className="estimator-no-data" style={{ marginBottom: '16px' }}>
                Based on current filters and timeframe.
            </p>

            <div className="estimate-result" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                {conditionData.bands.map((band) => (
                    <div key={band.label} style={{ marginBottom: '14px' }}>
                        <div className="estimate-confidence" style={{ color: band.tone }}>
                            {band.label}
                        </div>
                        <div className="estimate-range">
                            {formatPrice(band.low)} - {formatPrice(band.high)}
                        </div>
                    </div>
                ))}
                <div className="estimate-sample-size">
                    Based on {conditionData.sampleSize} result{conditionData.sampleSize !== 1 ? 's' : ''}
                </div>
            </div>

            <div className="condition-graph-toggle-row">
                <span className="condition-graph-toggle-label">Show on Graph</span>
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={showOnGraph}
                        onChange={(e) => setShowOnGraph(e.target.checked)}
                    />
                    <span className="slider round"></span>
                </label>
            </div>
        </div>
    );
};

export default ConditionEstimator;
